import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ArrowUpRightIcon, QuoteIcon } from 'lucide-react';
import { testimonials } from '../../data/testimonials';
import { clinic } from '../../data/clinic';
const EASE = [0.23, 1, 0.32, 1] as const;
export function ReviewsSection() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const review = testimonials[index];
  const total = testimonials.length;
  const go = (step: number) => {
    setDirection(step);
    setIndex(i => (i + step + total) % total);
  };
  return <section className="bg-ink text-white" aria-labelledby="ervaringen-titel" data-id="element-128" data-mp="1os7alkoh8d7e">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 lg:grid-cols-[0.8fr_1.6fr] lg:gap-20 lg:px-8 lg:py-28" data-id="element-129" data-mp="1ou9ynz62jw9h">
        <div className="flex flex-col" data-id="element-130" data-mp="dd0grh2l9caa">
          <h2 id="ervaringen-titel" className="font-display text-3xl font-semibold tracking-tight sm:text-4xl" data-id="element-131" data-mp="dbz10zamo1m8">
            Wat patiënten over ons zeggen
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-white/70" data-id="element-132" data-mp="1cp3dimtckbs6">
            Ervaringen van patiënten, gedeeld op ZorgkaartNederland.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-start" data-id="element-133" data-mp="xrkve3b6cwtp">
            <a href={clinic.zorgkaartUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-5 py-3 text-[15px] font-semibold text-ink transition-colors duration-150 hover:bg-primary-soft" data-id="element-134" data-mp="12pm908pu03xs">
              Vertel uw ervaring
              <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-135" data-mp="jzwe7lm2ji5v" />
            </a>
            <Link to="/ervaringen" className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-1 py-3 text-[15px] font-semibold text-cyan hover:text-white" data-id="element-136" data-mp="60eq78glk8xk">
              Alle ervaringen lezen
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-137" data-mp="2cr9v9baifikb" />
            </Link>
          </div>
        </div>

        <div data-id="element-138" data-mp="22zdkaznbrxyo">
          <QuoteIcon className="h-10 w-10 text-cyan" aria-hidden="true" data-id="element-139" data-mp="22z43o2o6735n" />
          <div className="relative mt-6 min-h-[260px] sm:min-h-[220px]" aria-live="polite" data-id="element-140" data-mp="22x14ot51hzos">
            <AnimatePresence mode="wait" initial={false} custom={direction} data-id="element-141" data-mp="1xy7sr55eigqq">
              <motion.figure key={index} custom={direction} initial={{
              opacity: 0,
              x: direction * 24
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -16
            }} transition={{
              duration: 0.22,
              ease: EASE
            }} data-id="element-142" data-mp="2vnj7dt1upz1o">
                <blockquote className="font-display text-2xl font-medium leading-snug sm:text-[32px] sm:leading-[1.3]" data-id="element-143" data-mp="1aaq6fp4woav5">
                  “{review.quote}”
                </blockquote>
                <figcaption className="mt-6 text-[15px] font-medium text-white/70" data-id="element-144" data-mp="16b7nv00k4bvh">{review.name}</figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>

          <div className="mt-10 flex items-center justify-between gap-6 border-t border-white/15 pt-6" data-id="element-145" data-mp="3lylc3ncwfv1m">
            <div className="flex gap-1.5" role="tablist" aria-label="Kies een ervaring" data-id="element-146" data-mp="3qvtunp1ym13e">
              {testimonials.map((t, i) => <button key={t.name} type="button" role="tab" aria-selected={i === index} aria-label={`Ervaring van ${t.name}`} onClick={() => {
              setDirection(i > index ? 1 : -1);
              setIndex(i);
            }} className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ${i === index ? 'w-8 bg-cyan' : 'w-3 bg-white/25 hover:bg-white/50'}`} data-id="element-147" data-mp="4htxt7yq38af" data-mpi={i} />)}
            </div>
            <div className="flex items-center gap-3" data-id="element-148" data-mp="1oxqcmt6xotlk">
              <span className="text-sm tabular-nums text-white/60" data-id="element-149" data-mp="1p0ckyv1nfd4u">
                {index + 1} / {total}
              </span>
              <button type="button" onClick={() => go(-1)} className="flex h-11 w-11 items-center justify-center rounded-full ring-1 ring-white/25 transition-colors duration-150 hover:bg-white hover:text-ink" aria-label="Vorige ervaring" data-id="element-150" data-mp="1ywfoo5c5fjtw">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-151" data-mp="119qfnbj146yj" />
              </button>
              <button type="button" onClick={() => go(1)} className="flex h-11 w-11 items-center justify-center rounded-full ring-1 ring-white/25 transition-colors duration-150 hover:bg-white hover:text-ink" aria-label="Volgende ervaring" data-id="element-152" data-mp="1fy3au6aallrz">
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-153" data-mp="iho5mj0w7ox9" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}